import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Keyboard, Pagination, Mousewheel, Navigation } from "swiper/modules";
import Iconify from "@/components/ui/Iconify";
import FullScreenImageSlider from "@/components/content-modal-comps/fullscreen-image-slider";
import LivePhotoPlayer from "@/components/ui/LivePhotoPlayer";
import {
  getMediaRecordPath,
  getMediaUrl,
} from "@/utils/media";

const ContentImageMediaSlider = ({ slides }) => {
  const imgRef = useRef(null);
  const swiperRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isFullScreenModalOpen, setIsFullScreenModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  function getPostMediaUrl(path) {
    if (!path) return "";
    return getMediaUrl(path);
  }

  function getSlideImageUrl(slide) {
    const path = getMediaRecordPath(slide, { variant: "large" });
    return getPostMediaUrl(path);
  }

  const BlurredImageFill = ({ src }) => {
    if (!src) return null;

    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden bg-black"
        aria-hidden="true"
      >
        <img
          src={src}
          alt=""
          className="h-full w-full scale-[1.1] object-cover opacity-[0.55] blur-[20px] brightness-[0.84]"
        />
        <div className="absolute inset-0 bg-black/[0.16]" />
      </div>
    );
  };

  return (
    <div className="relative group w-full h-full max-w-full bg-black select-none">
      {slides?.length > 1 ? (
        <Swiper
          ref={swiperRef}
          style={{
            "--swiper-navigation-color": "#fff",
            "--swiper-pagination-color": "rgba(255,255,255,0.9)",
            "--swiper-pagination-bullet-inactive-color": "rgba(255,255,255,0.58)",
            "--swiper-pagination-bullet-inactive-opacity": "1",
          }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex + 1)} // Update current slide on change
          slidesPerView={1}
          spaceBetween={30}
          keyboard={{
            enabled: true,
          }}
          mousewheel={true}
          pagination={{
            clickable: true,
          }}
          lazy="true"
          navigation={{
            prevEl: ".custom-prev",
            nextEl: ".custom-next",
          }}
          modules={[Keyboard, Mousewheel, Pagination, Navigation]}
          className="content-image-media-swiper w-auto max-w-full h-full"
        >
          {slides?.map((slide, index) => {
            const imageUrl = getSlideImageUrl(slide);

            return (
              <SwiperSlide
                key={index}
                className="w-fit max-w-full h-full flex items-center justify-center relative overflow-hidden bg-black"
              >
                {slide?.mediaType === "livephoto" ? (
                  <div className="w-full h-full flex items-center justify-center relative z-[1] pointer-events-auto">
                    <LivePhotoPlayer
                      photoUrl={getPostMediaUrl(slide?.livePhotoImagePath)}
                      videoUrl={getPostMediaUrl(slide?.livePhotoVideoPath)}
                      autoplay // Enable autoplay on intial model open
                      skipLiveBadge={false}
                    />
                  </div>
                ) : (
                  <>
                  <BlurredImageFill src={imageUrl} />
                  <img
                    src={imageUrl}
                    ref={imgRef}
                    alt="slide"
                    className="relative z-[1] mx-auto max-w-full h-full object-contain cursor-zoom-in"
                    onClick={() => setIsFullScreenModalOpen(true)}
                    onLoad={() => setIsLoaded(true)}
                  />
                  </>
                )}
                {!isLoaded && (
                  <div
                    className={`absolute top-0 left-0 right-0 bottom-0 ${isLoaded ? "opacity-0" : "opacity-100"
                      } transition ease-in-out duration-150`}
                  >
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt="post-card-media"
                        className={`relative z-[2] w-full h-full object-contain`}
                      />
                    )}
                  </div>
                )}
              </SwiperSlide>
            )
          })}
        </Swiper>
      )
        :
        slides?.map((slide, index) => {
          const imageUrl = getSlideImageUrl(slide);

          return (
            <div
              key={index}
              className={`w-full max-w-full h-full flex items-center justify-center relative overflow-hidden bg-black`}
            >
              {slide?.mediaType === "livephoto" ? (
                <div className="w-full h-full flex items-center justify-center relative z-[1] pointer-events-auto">
                  <LivePhotoPlayer
                    photoUrl={getPostMediaUrl(slide?.livePhotoImagePath)}
                    videoUrl={getPostMediaUrl(slide?.livePhotoVideoPath)}
                    autoplay // Enable autoplay on intial model open
                    skipLiveBadge={false}
                  />
                </div>
              ) : (
                <>
                <BlurredImageFill src={imageUrl} />
                <img
                  src={imageUrl}
                  ref={imgRef}
                  alt="slide"
                  className="relative z-[1] mx-auto max-w-full h-full object-contain cursor-zoom-in"
                  onClick={() => setIsFullScreenModalOpen(true)}
                  onLoad={() => setIsLoaded(true)}
                />
                </>
              )}
              {!isLoaded && slide?.mediaType !== "livephoto" && (
                <div
                  className={`absolute top-0 left-0 right-0 bottom-0 ${isLoaded ? "opacity-0" : "opacity-100"
                    } transition ease-in-out duration-150`}
                >
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="post-card-media"
                      className={`relative z-[2] w-full h-full object-contain`}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })
      }

      {/* Pagination */}
      <div
        className={`${slides?.length > 1 ? "flex" : "hidden"
          } invisible group-hover:visible justify-center items-center rounded-full bg-black bg-opacity-15 py-1 px-2 absolute top-5 lg:top-8 right-7 lg:right-5 transform -translate-y-1/2 text-white z-50`}
      >
        <span className={"!text-dark_secondary_label text-xs z-50"}>
          {currentSlide}/{slides?.length}
        </span>
      </div>

      {/* Custom navigation buttons */}
      <div
        className={`${slides?.length > 1 ? "flex" : "hidden"
          } custom-prev invisible group-hover:visible absolute left-0 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-start`}
      >
        <button className="bg-black bg-opacity-15 p-1 rounded-full ml-3">
          <Iconify
            icon={"iconamoon:arrow-left-2-duotone"}
            className="!w-6 !h-6 !text-dark_secondary_label"
          />
        </button>
      </div>
      <div
        className={`${slides?.length > 1 ? "flex" : "hidden"
          } custom-next invisible group-hover:visible absolute right-0 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-end`}
      >
        <button className="bg-black bg-opacity-15 p-1 rounded-full mr-3">
          <Iconify
            icon={"iconamoon:arrow-right-2-duotone"}
            className="!w-6 !h-6 !text-dark_secondary_label"
          />
        </button>
      </div>
      <FullScreenImageSlider
        isOpen={isFullScreenModalOpen}
        setIsOpen={setIsFullScreenModalOpen}
        slides={slides}
      />
      <style jsx global>{`
        .content-image-media-swiper .swiper-pagination {
          z-index: 45;
        }

        .content-image-media-swiper .swiper-pagination-bullet {
          width: 7px;
          height: 7px;
          box-shadow:
            0 1px 6px rgba(0, 0, 0, 0.14),
            0 0 10px rgba(255, 255, 255, 0.18);
        }

        .content-image-media-swiper .swiper-pagination-bullet-active {
          transform: scale(1.04);
          box-shadow:
            0 1px 8px rgba(0, 0, 0, 0.16),
            0 0 12px rgba(255, 255, 255, 0.24);
        }
      `}</style>
    </div>
  );
};

export default ContentImageMediaSlider;
